import type { SupportedSession } from "../types";
import { useContext } from "react";
import { TellescopeSessionContext } from "./context";

/**
 * Hook to access the Tellescope SDK session from context or a directly provided session.
 *
 * This hook provides flexible access to the session by checking in order:
 * 1. A session passed directly as a parameter
 * 2. A session from the TellescopeQueryProvider context
 *
 * @param providedSession - Optional session to use instead of the context session.
 *   Accepts both `Session` (user) and `EnduserSession` (enduser) types.
 * @returns The Tellescope SDK session instance, or null if explicitly set to null in the provider
 *
 * @throws {Error} If no session is found in context (context is undefined) and none is provided
 *
 * @example
 * ```tsx
 * // Using context session
 * const session = useTellescopeSession();
 *
 * // Using directly provided session
 * const session = useTellescopeSession(mySession);
 *
 * // Explicitly null session (queries will be disabled)
 * const session = useTellescopeSession(); // returns null if provider has session={null}
 * ```
 */
export const useTellescopeSession = (providedSession?: SupportedSession | null): SupportedSession | null => {
  const contextSession = useContext(TellescopeSessionContext);

  // If a session is explicitly provided (including null), use it
  if (providedSession !== undefined) return providedSession;

  // If context session is null, that's an explicit "no session" state
  if (contextSession === null) return null;

  // If context session exists and is not null, use it
  if (contextSession) return contextSession;

  // If context session is undefined, no provider was found
  throw new Error(
    "Tellescope session not found. Either:\n" +
      "1. Wrap your app in <TellescopeQueryProvider session={...} />, or\n" +
      "2. Pass session directly to the hook via the session prop"
  );
};
